import { useState } from 'react';
import {
  Lock,
  ShieldCheck,
  X,
  FloppyDisk,
  Spinner,
  WarningCircle,
  ClockCounterClockwise,
  MagnifyingGlass,
  Phone,
  UserPlus,
} from '@phosphor-icons/react';
import { API } from './constants';
import { useRef, useCallback, useEffect } from 'react';

// ==================== CLIENT SEARCH (reused) ====================
export const ClienteSearchField = ({ value, onChange, onSelect }) => {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/clientes/search/quick?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setResults(data);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { doSearch(''); }, [doSearch]);

  const handleChange = (text) => {
    setQuery(text);
    onChange(text);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(text), 200);
  };

  const handleSelect = (cliente) => {
    setQuery(cliente.nome);
    onSelect(cliente);
    setOpen(false);
  };

  const handleFocus = () => {
    setOpen(true);
    if (!query) doSearch('');
  };

  const noMatch = query.trim().length >= 2 && !loading && results.length === 0;

  return (
    <div className="relative">
      <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Cliente *</label>
      <div className="relative">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
        <input
          data-testid="input-cliente-nome"
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={handleFocus}
          placeholder="Buscar por nome ou telefone..."
          autoComplete="off"
          className="w-full pl-9 pr-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
        />
        {loading && <Spinner size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#A1A1AA]" />}
      </div>

      {open && (results.length > 0 || noMatch) && (
        <div
          data-testid="cliente-search-results"
          className="absolute z-50 w-full mt-1 bg-white border border-[#E4E4E7] rounded-xl shadow-lg max-h-52 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handleSelect(c)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#F7F6F4] transition-colors text-left"
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0" style={{ backgroundColor: '#1B4332' }}>
                {c.nome?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#18181B] truncate">{c.nome}</p>
                {c.telefone && (
                  <p className="text-[10px] text-[#A1A1AA] flex items-center gap-1">
                    <Phone size={9} weight="regular" />{c.telefone}
                  </p>
                )}
              </div>
            </button>
          ))}
          {noMatch && (
            <div className="px-3 py-3 text-center">
              <p className="text-xs text-[#A1A1AA] mb-1">Nenhum cliente encontrado</p>
              <div className="flex items-center justify-center gap-1 text-[11px] font-medium text-[#1B4332]">
                <UserPlus size={12} weight="bold" />
                <span>Sera registrado como novo</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ==================== AJUSTE GERENCIAL MODAL ====================
export const AjusteGerencialModal = ({ selectedDate, profissionais, onClose, onSuccess, mode = 'agendamento' }) => {
  const [step, setStep] = useState('password'); // password | form
  const [senhaMaster, setSenhaMaster] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Agendamento form
  const [form, setForm] = useState({
    cliente_nome: '',
    cliente_id: '',
    profissional_id: '',
    hora_inicio: '10:00',
    servicos: '',
    observacoes: '',
  });

  // Status form
  const [statusForm, setStatusForm] = useState({
    profissional_id: '',
    status: 'produziu',
  });

  const handlePasswordValidate = async () => {
    if (!senhaMaster.trim()) { setPasswordError('Digite a senha master'); return; }
    setValidating(true);
    setPasswordError('');
    try {
      const res = await fetch(`${API}/governance/validate-master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senha_master: senhaMaster }),
      });
      if (!res.ok) {
        setPasswordError('Senha master incorreta');
        return;
      }
      setStep('form');
    } catch {
      setPasswordError('Erro ao validar senha');
    } finally { setValidating(false); }
  };

  const handleSubmitAgendamento = async (e) => {
    e.preventDefault();
    if (!form.cliente_nome.trim() || !form.profissional_id || !justificativa.trim()) return;
    setSaving(true);
    try {
      const body = {
        senha_master: senhaMaster,
        justificativa: justificativa,
        agendamento: {
          ...form,
          data: selectedDate,
          servicos: form.servicos.split(',').map(s => s.trim()).filter(Boolean),
        },
      };
      const res = await fetch(`${API}/cod/agendamentos/retroativo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed');
      onSuccess();
    } catch { /* */ } finally { setSaving(false); }
  };

  const handleSubmitStatus = async (e) => {
    e.preventDefault();
    if (!statusForm.profissional_id || !justificativa.trim()) return;
    setSaving(true);
    try {
      const body = {
        senha_master: senhaMaster,
        justificativa: justificativa,
        profissional_id: statusForm.profissional_id,
        data: selectedDate,
        status: statusForm.status,
      };
      const res = await fetch(`${API}/cod/status-diario/retroativo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed');
      onSuccess();
    } catch { /* */ } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ajuste-gerencial-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b" style={{ borderColor: '#C9A96230' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#C9A96220' }}>
              <ShieldCheck size={22} weight="fill" style={{ color: '#C9A962' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
                Ajuste Gerencial
              </h2>
              <p className="text-[11px] text-[#71717A]">Registro retroativo com auditoria</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Date context */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed" style={{ borderColor: '#C9A96240', backgroundColor: '#C9A96208' }}>
            <ClockCounterClockwise size={14} weight="bold" style={{ color: '#C9A962' }} />
            <span className="text-xs font-medium" style={{ color: '#8B7340' }}>
              Data retroativa: {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>

        {step === 'password' ? (
          /* STEP 1: Password */
          <div className="p-6 space-y-4">
            <div className="text-center mb-2">
              <Lock size={32} weight="light" className="mx-auto mb-2" style={{ color: '#C9A962' }} />
              <p className="text-sm text-[#71717A]">
                Operacoes retroativas exigem autorizacao gerencial.
              </p>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Senha Master</label>
              <input
                data-testid="input-senha-master"
                type="password"
                value={senhaMaster}
                onChange={(e) => { setSenhaMaster(e.target.value); setPasswordError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordValidate()}
                placeholder="Digite a senha do gerente"
                autoFocus
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30"
              />
              {passwordError && (
                <div className="flex items-center gap-1.5 mt-2 text-red-500">
                  <WarningCircle size={14} weight="fill" />
                  <span className="text-xs font-medium">{passwordError}</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">
                Cancelar
              </button>
              <button
                data-testid="btn-validar-senha"
                onClick={handlePasswordValidate}
                disabled={validating || !senhaMaster.trim()}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                style={{ backgroundColor: '#C9A962' }}
              >
                {validating ? <Spinner size={16} className="animate-spin" /> : <ShieldCheck size={16} weight="bold" />}
                {validating ? 'Validando...' : 'Autorizar'}
              </button>
            </div>
          </div>
        ) : (
          /* STEP 2: Form + Justification */
          <div className="p-6">
            {/* Justification (always required) */}
            <div className="mb-4">
              <label className="block text-[10px] font-medium uppercase tracking-wide mb-1" style={{ color: '#C9A962' }}>
                Justificativa *
              </label>
              <textarea
                data-testid="input-justificativa"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                placeholder="Descreva o motivo do ajuste retroativo..."
                rows={2}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border rounded-xl text-sm focus:outline-none focus:ring-2 resize-none"
                style={{ borderColor: '#C9A96240', focusRingColor: '#C9A96230' }}
              />
            </div>

            {/* Mode-specific form */}
            {mode === 'agendamento' ? (
              <form onSubmit={handleSubmitAgendamento} className="space-y-4">
                <ClienteSearchField
                  value={form.cliente_nome}
                  onChange={(nome) => setForm(p => ({ ...p, cliente_nome: nome, cliente_id: '' }))}
                  onSelect={(c) => setForm(p => ({ ...p, cliente_nome: c.nome, cliente_id: c.id }))}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Profissional *</label>
                    <select data-testid="retroativo-select-profissional" value={form.profissional_id}
                      onChange={(e) => setForm(p => ({ ...p, profissional_id: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/20"
                    >
                      <option value="">Selecione...</option>
                      {profissionais.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Horario</label>
                    <input data-testid="retroativo-input-hora" type="time" value={form.hora_inicio}
                      onChange={(e) => setForm(p => ({ ...p, hora_inicio: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/20"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Servicos</label>
                  <input data-testid="retroativo-input-servicos" type="text" value={form.servicos}
                    onChange={(e) => setForm(p => ({ ...p, servicos: e.target.value }))}
                    placeholder="Corte, Escova, Coloracao..."
                    className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/20"
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={onClose}
                    className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">
                    Cancelar
                  </button>
                  <button type="submit"
                    disabled={saving || !form.cliente_nome.trim() || !form.profissional_id || justificativa.trim().length < 5}
                    data-testid="btn-salvar-retroativo"
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                    style={{ backgroundColor: '#C9A962' }}
                  >
                    {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                    {saving ? 'Salvando...' : 'Registrar Ajuste'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmitStatus} className="space-y-4">
                <div>
                  <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Profissional *</label>
                  <select data-testid="retroativo-select-profissional" value={statusForm.profissional_id}
                    onChange={(e) => setStatusForm(p => ({ ...p, profissional_id: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/20"
                  >
                    <option value="">Selecione...</option>
                    {profissionais.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Status</label>
                  <select data-testid="retroativo-select-status" value={statusForm.status}
                    onChange={(e) => setStatusForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/20"
                  >
                    <option value="produziu">Produziu</option>
                    <option value="disponivel">Disponivel</option>
                    <option value="ausente">Ausente</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={onClose}
                    className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">
                    Cancelar
                  </button>
                  <button type="submit"
                    disabled={saving || !statusForm.profissional_id || justificativa.trim().length < 5}
                    data-testid="btn-salvar-status-retroativo"
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                    style={{ backgroundColor: '#C9A962' }}
                  >
                    {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                    {saving ? 'Salvando...' : 'Registrar Ajuste'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== RETROACTIVE BADGE ====================
export const RetroativoBadge = ({ justificativa }) => {
  return (
    <span
      data-testid="retroativo-badge"
      className="relative group/retro inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: '#C9A96215', color: '#C9A962' }}
    >
      <ClockCounterClockwise size={10} weight="bold" />
      Ajuste
      {justificativa && (
        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#18181B] text-white text-[11px] font-normal normal-case tracking-normal rounded-lg whitespace-nowrap opacity-0 group-hover/retro:opacity-100 transition-opacity duration-200 shadow-lg z-30 max-w-[200px] text-center">
          {justificativa}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#18181B]" />
        </span>
      )}
    </span>
  );
};
